import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { FeedbackApi, FeedbackResult, FeedbackSubmission } from './types';

/** Thrown for any non-2xx answer, carrying a message fit for the user. */
export class FeedbackApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'FeedbackApiError';
  }
}

/**
 * Two shapes reach the client: the plugin's own handlers answer
 * `{ error: "text" }`, while anything rejected before them by Backstage's
 * middleware (expired session → 401, oversized body → 413, …) answers
 * `{ error: { name, message } }`.
 */
export function errorMessageFrom(body: unknown, status: number): string {
  if (status === 401) {
    return 'Your session has expired. Sign in again and resend.';
  }
  const err = (body as { error?: unknown } | undefined)?.error;
  if (typeof err === 'string' && err) return err;
  const nested = (err as { message?: unknown } | undefined)?.message;
  if (typeof nested === 'string' && nested) return nested;
  return `Feedback API failed: HTTP ${status}`;
}

/** Multipart field carrying the JSON submission (mirrors the backend). */
export const PAYLOAD_FIELD = 'payload';
/** Multipart field carrying each screenshot (mirrors the backend). */
export const SCREENSHOTS_FIELD = 'screenshots';

/**
 * The request body for one submission: plain JSON when there is nothing to
 * upload, multipart with a `payload` JSON field plus `screenshots` files
 * otherwise. Exported for tests.
 */
export function buildRequestBody(submission: FeedbackSubmission): {
  body: BodyInit;
  headers: Record<string, string>;
} {
  const { screenshots = [], ...fields } = submission;
  if (!screenshots.length) {
    return {
      body: JSON.stringify(fields),
      headers: { 'Content-Type': 'application/json' },
    };
  }
  const form = new FormData();
  form.append(PAYLOAD_FIELD, JSON.stringify(fields));
  for (const file of screenshots) {
    form.append(SCREENSHOTS_FIELD, file, file.name);
  }
  // No Content-Type: the browser sets multipart/form-data with the boundary.
  return { body: form, headers: {} };
}

export class FeedbackClient implements FeedbackApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async submit(submission: FeedbackSubmission): Promise<FeedbackResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('feedback');
    const { body, headers } = buildRequestBody(submission);
    const response = await this.fetchApi.fetch(baseUrl, {
      method: 'POST',
      headers,
      body,
    });
    if (!response.ok) {
      const parsed = await response.json().catch(() => undefined);
      throw new FeedbackApiError(
        errorMessageFrom(parsed, response.status),
        response.status,
      );
    }
    return response.json();
  }
}
